import { FC, useEffect, useRef, useState } from 'react';

import { Icon } from '@shared/components/icons';
import { useAuth } from '@shared/state/hooks/useAuth';
import { UserRole } from '@shared/types';

interface StoreUserMenuButtonProps {
  /**
   * 지정하면 fixed 오버레이로 렌더한다(값 = Tailwind top-* 클래스, 세로 위치).
   * - gift-store처럼 navbar 우측이 아이콘으로 차 있어 그 자리에 넣을 수 없을 때 사용. (예: `top-16`)
   * 미지정이면 부모 레이아웃에 inline으로 렌더한다.
   * - flower-store StoreNavBar의 비어 있는 우측 슬롯에 그대로 삽입할 때 사용.
   */
  floatingTopClassName?: string;
}

/**
 * 권한 role(Admin/StoreSeller/대행사/기프트 등)로 로그인한 유저에게 노출되는 우측 상단 유저 버튼.
 * 본인 이름을 보여주고, 클릭하면 로그아웃 버튼이 펼쳐진다.
 *
 * 노출 규칙:
 * - end-customer role(StoreCustomer/StoreGuest/BugoCustomer/BugoGuest)만 가진 "평범한 구매자"는
 *   StoreCustomer/StoreGuest가 되는 순간 숨긴다.
 * - 그 외 권한 role을 하나라도 가지면, 구매자(StoreCustomer)가 되어도 계속 표시한다.
 * - 비로그인(userProfile 없음)도 표시하지 않는다.
 */
export const StoreUserMenuButton: FC<StoreUserMenuButtonProps> =
  function StoreUserMenuButton({ floatingTopClassName }) {
    const { User } = Icon();
    const { userProfile, logout } = useAuth();
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // 바깥 클릭 시 닫기 (navbar 인라인 렌더에서도 동작하도록 backdrop 대신 ref 사용)
    useEffect(() => {
      if (!open) return;
      const handler = (e: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(e.target as Node)
        ) {
          setOpen(false);
        }
      };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    if (!userProfile) return null;

    const roles = userProfile.roles ?? [];

    // 일반 end-customer role (이것들만 가지면 평범한 구매자로 간주)
    const END_CUSTOMER_ROLES: UserRole[] = [
      UserRole.StoreCustomer,
      UserRole.StoreGuest,
      UserRole.BugoCustomer,
      UserRole.BugoGuest,
    ];
    // Admin/StoreSeller/대행사/기프트 등 "권한" role 보유 여부
    const hasElevatedRole = roles.some((r) => !END_CUSTOMER_ROLES.includes(r));
    const isStoreParticipant =
      roles.includes(UserRole.StoreCustomer) ||
      roles.includes(UserRole.StoreGuest);

    // 권한 role이 있으면 구매자가 되어도 계속 표시.
    // 권한 role이 없는 평범한 구매자(StoreCustomer/StoreGuest)는 숨김.
    if (!hasElevatedRole && isStoreParticipant) {
      return null;
    }

    const menu = (
      <div ref={containerRef} className="relative">
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className="flex items-center gap-1 rounded-full border py-1.5 pl-2 pr-3 text-xs font-bold shadow-sm theme-text-1 theme-bg-1 theme-border-1"
        >
          <span className="[&>svg]:h-4 [&>svg]:w-4">
            <User />
          </span>
          <span className="max-w-[120px] truncate">{userProfile.info?.name}님</span>
        </button>
        {open && (
          <div className="absolute right-0 z-30 mt-1 w-32 overflow-hidden rounded-lg border shadow-md theme-bg-1 theme-border-1">
            <button
              type="button"
              onClick={() => logout()}
              className="block w-full px-4 py-2.5 text-left text-sm theme-text-8 hover:bg-gray-50"
            >
              로그아웃
            </button>
          </div>
        )}
      </div>
    );

    // inline: navbar 우측 빈 슬롯 등 부모 레이아웃에 그대로 삽입
    if (!floatingTopClassName) {
      return menu;
    }

    // floating: 중앙 정렬 max-w-screen-md 컨테이너 기준 우측에 fixed 오버레이로 표시
    return (
      <div
        className={`pointer-events-none fixed inset-x-0 ${floatingTopClassName} z-40 mx-auto max-w-screen-md px-3`}
      >
        <div className="flex justify-end">
          <div className="pointer-events-auto">{menu}</div>
        </div>
      </div>
    );
  };
